/**
 * Local File Helper Utility - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A comprehensive local file loading utility with advanced features:
 * - Local JSON file loading from data folder
 * - API fallback when local files unavailable
 * - Available file registry and validation
 * - Development and testing mode support
 * - Environment variable-based configuration
 * - Error handling with graceful fallback
 * - File existence checking and validation
 * 
 * Features:
 * - Dynamic import of JSON files from local data folder
 * - File availability checking before loading
 * - Comprehensive error handling and warnings
 * - Environment-based loading configuration
 * - Fallback to API loading when files missing
 * - File registry for available local files
 * - Developer-friendly error messages
 * 
 * Available Local Files:
 * - Navigation and timeline data
 * - News and media content
 * - External information sources
 * - Innovation radar data
 * - Video gallery content
 * - Policy and regulatory updates
 * 
 * Configuration:
 * - REACT_APP_USE_LOCAL_FILES=true (environment variable)
 * - REACT_APP_LOCAL_FILE_MODE=true (alternative env variable)
 * - Automatic fallback to API when files unavailable
 * 
 * Usage:
 * import localFileHelper from './localFileHelper';
 * const data = await localFileHelper.loadLocalJSONFile('timeline.json');
 * const isAvailable = localFileHelper.isFileAvailableLocally('news.json');
 * 
 * @returns {Object} Local file loading methods with fallback support
 */
/**
 * Local File Helper - Utility for loading JSON files from local data folder
 * This provides an alternative to API-based file loading for development and testing
 */

// Available local JSON files - defined once to avoid repetition
const AVAILABLE_LOCAL_FILES = [
  'navigationStructure.json',
  'innovation-radar-data.json', 
  'timeline.json',
  'videoGallery.json',
  'monthlyNews.json',
  'featured_and_submitted_articles.json',
  'news.json',
  'external_info_sources_categories.json',
  'external_info_sources.json',
  'key_policy_messages.json',
  'cbi_quarterly_updates.json'
];

const localFileHelper = {
  /**
   * Load JSON file from the local data folder
   * @param {string} fileName - Name of the JSON file to load
   * @returns {Promise<Object>} - Promise resolving to file content
   */
  loadLocalJSONFile: async (fileName) => {
    try {
      // Import the JSON file dynamically from the data folder
      const fileContent = await import(`../data/${fileName}`);
      return { fileContent: fileContent.default || fileContent };
    } catch (error) {
      console.error(`Error loading local JSON file ${fileName}:`, error);
      
      // Check if file exists in data folder
      if (!AVAILABLE_LOCAL_FILES.includes(fileName)) {
        console.warn(`File ${fileName} not found in local data folder. Available files:`, AVAILABLE_LOCAL_FILES);
        console.warn('You may need to add this file to the data folder or use API loading instead.');
      }
      
      throw new Error(`Failed to load local file: ${fileName}`);
    }
  },

  /**
   * Check if a file exists in the local data folder
   * @param {string} fileName - Name of the file to check
   * @returns {boolean} - True if file exists locally
   */
  isFileAvailableLocally: (fileName) => {
    return AVAILABLE_LOCAL_FILES.includes(fileName);
  },

  /**
   * Get list of available local JSON files
   * @returns {Array<string>} - Array of available file names
   */
  getAvailableLocalFiles: () => {
    return [...AVAILABLE_LOCAL_FILES]; // Return a copy to prevent mutation
  }
};

export default localFileHelper; 